import type { Account } from '@repo-level/core-profile/api/profile';
import produce from 'immer';
import type { CompPoints, PlayerSettings, User, Wallet } from '../../api/auth';

export enum REASON_FOR_UPDATING_USER {
  VISIT = 'VISIT',
  LOGIN = 'LOGIN',
  REGISTRATION = 'REGISTRATION',
  DEPOSIT = 'DEPOSIT',
  REFRESH_STATUSES = 'REFRESH_STATUSES',
  REFRESH_WALLETS = 'REFRESH_WALLETS',
}

export type UserState = {
  user: User | undefined;
  account: Account | null;
  compPoints: CompPoints | undefined;
  reasonForUpdatingUser: REASON_FOR_UPDATING_USER | undefined;
  isAuthenticated: boolean;
  pendingUser: boolean;

  wallets: Wallet[];
  extendedWallets: Wallet[];
  pendingWallets: boolean;

  currentAmount: string | number | null;

  afterLoginRedirectUrl: string | undefined;
  pendingPlayerSettings: boolean;
  playerSettings: PlayerSettings | undefined;

  pendingSubmitPayment: boolean;

  currentPaymentMethodId: string | undefined;
};

export enum USER_ACTION_TYPE {
  USER_FETCH = 0,
  USER_INSERT = 1,
  USER_RESET = 2,
  USER_START_PENDING = 3,
  USER_STOP_PENDING = 4,
  USER_UPDATE_REASON = 5,
  USER_REFRESH = 6,

  WALLETS_FETCH = 7,
  WALLETS_UPDATE = 8,
  WALLETS_START_PENDING = 9,
  WALLETS_STOP_PENDING = 10,

  SET_ACCOUNT = 11,
  SET_CURRENT_AMOUNT = 12,

  AFTER_LOGIN_REDIRECT_SET = 13,
  AFTER_LOGIN_REDIRECT_RESET = 14,

  PLAYER_SETTINGS_FETCH = 15,
  PLAYER_SETTINGS_START_PENDING = 16,
  PLAYER_SETTINGS_STOP_PENDING = 17,

  PLAYER_SUBMIT_PAYMENT_START_PENDING = 18,
  PLAYER_SUBMIT_PAYMENT_STOP_PENDING = 19,
  SET_CURRENT_PAYMENT_METHOD_ID = 20,
}

export type UserAction =
  | {
      type: USER_ACTION_TYPE.USER_FETCH;
      payload: {
        user: User | undefined;
        compPoints: CompPoints | undefined;
        reasonForUpdatingUser: REASON_FOR_UPDATING_USER | undefined;
        isAuthenticated: boolean;
        wallets: Wallet[];
        extendedWallets: Wallet[];
      };
    }
  | {
      type: USER_ACTION_TYPE.WALLETS_UPDATE;
      payload: {
        wallets: Wallet[];
        extendedWallets: Wallet[];
      };
    }
  | {
      type: USER_ACTION_TYPE.SET_ACCOUNT;
      payload: { account: Account | null };
    }
  | {
      type: USER_ACTION_TYPE.SET_CURRENT_AMOUNT;
      payload: { currentAmount: string | number | null };
    }
  | { type: USER_ACTION_TYPE.USER_REFRESH; payload: User }
  | {
      type: USER_ACTION_TYPE.USER_INSERT;
      payload: {
        user: User | undefined;
        compPoints: CompPoints | undefined;
        reasonForUpdatingUser: REASON_FOR_UPDATING_USER;
        isAuthenticated: boolean;
        wallets: Wallet[];
      };
    }
  | { type: USER_ACTION_TYPE.USER_RESET }
  | {
      type: USER_ACTION_TYPE.USER_UPDATE_REASON;
      payload: { reasonForUpdatingUser: REASON_FOR_UPDATING_USER };
    }
  | { type: USER_ACTION_TYPE.USER_START_PENDING }
  | { type: USER_ACTION_TYPE.USER_STOP_PENDING }
  | { type: USER_ACTION_TYPE.WALLETS_START_PENDING }
  | { type: USER_ACTION_TYPE.WALLETS_STOP_PENDING }
  | {
      type: USER_ACTION_TYPE.AFTER_LOGIN_REDIRECT_SET;
      payload: { url: string };
    }
  | { type: USER_ACTION_TYPE.AFTER_LOGIN_REDIRECT_RESET }
  | { type: USER_ACTION_TYPE.PLAYER_SETTINGS_START_PENDING }
  | { type: USER_ACTION_TYPE.PLAYER_SETTINGS_STOP_PENDING }
  | { type: USER_ACTION_TYPE.PLAYER_SUBMIT_PAYMENT_START_PENDING }
  | { type: USER_ACTION_TYPE.PLAYER_SUBMIT_PAYMENT_STOP_PENDING }
  | {
      type: USER_ACTION_TYPE.PLAYER_SETTINGS_FETCH;
      payload: PlayerSettings;
    }
  | {
      type: USER_ACTION_TYPE.SET_CURRENT_PAYMENT_METHOD_ID;
      payload: { currentPaymentMethodId: string | undefined };
    };

type UserReducer = (state: UserState, action: UserAction) => UserState;

export const userReducer: UserReducer = (state, action) =>
  produce(state, (draft) => {
    switch (action.type) {
      case USER_ACTION_TYPE.USER_FETCH:
        draft.user = action.payload.user;
        draft.reasonForUpdatingUser = action.payload.reasonForUpdatingUser;
        draft.isAuthenticated = action.payload.isAuthenticated;
        draft.wallets = action.payload.wallets;
        draft.extendedWallets = action.payload.extendedWallets;
        draft.compPoints = action.payload.compPoints;
        break;

      case USER_ACTION_TYPE.WALLETS_UPDATE:
        draft.wallets = action.payload.wallets;
        draft.extendedWallets = action.payload.extendedWallets;
        break;

      case USER_ACTION_TYPE.USER_REFRESH:
        draft.user = action.payload;
        draft.reasonForUpdatingUser = REASON_FOR_UPDATING_USER.REFRESH_STATUSES;
        break;

      case USER_ACTION_TYPE.USER_INSERT:
        draft.user = action.payload.user;
        draft.reasonForUpdatingUser = action.payload.reasonForUpdatingUser;
        draft.isAuthenticated = action.payload.isAuthenticated;
        draft.wallets = action.payload.wallets;
        draft.compPoints = action.payload.compPoints;
        break;

      case USER_ACTION_TYPE.USER_RESET:
        draft.user = undefined;
        draft.reasonForUpdatingUser = REASON_FOR_UPDATING_USER.VISIT;
        draft.isAuthenticated = false;
        draft.wallets = [];
        break;

      case USER_ACTION_TYPE.USER_UPDATE_REASON:
        draft.reasonForUpdatingUser = action.payload.reasonForUpdatingUser;
        break;

      case USER_ACTION_TYPE.USER_START_PENDING:
        draft.pendingUser = true;
        break;

      case USER_ACTION_TYPE.USER_STOP_PENDING:
        draft.pendingUser = false;
        break;

      case USER_ACTION_TYPE.WALLETS_START_PENDING:
        draft.pendingWallets = true;
        break;

      case USER_ACTION_TYPE.WALLETS_STOP_PENDING:
        draft.pendingWallets = false;
        break;

      case USER_ACTION_TYPE.SET_ACCOUNT:
        draft.account = action.payload.account;
        break;

      case USER_ACTION_TYPE.SET_CURRENT_AMOUNT:
        draft.currentAmount = action.payload.currentAmount;
        break;

      case USER_ACTION_TYPE.AFTER_LOGIN_REDIRECT_SET:
        draft.afterLoginRedirectUrl = action.payload.url;
        break;

      case USER_ACTION_TYPE.AFTER_LOGIN_REDIRECT_RESET:
        draft.afterLoginRedirectUrl = undefined;
        break;

      case USER_ACTION_TYPE.PLAYER_SETTINGS_START_PENDING:
        draft.pendingPlayerSettings = true;
        break;

      case USER_ACTION_TYPE.PLAYER_SETTINGS_STOP_PENDING:
        draft.pendingPlayerSettings = false;
        break;

      case USER_ACTION_TYPE.PLAYER_SUBMIT_PAYMENT_START_PENDING:
        draft.pendingSubmitPayment = true;
        break;

      case USER_ACTION_TYPE.PLAYER_SUBMIT_PAYMENT_STOP_PENDING:
        draft.pendingSubmitPayment = false;
        break;

      case USER_ACTION_TYPE.PLAYER_SETTINGS_FETCH:
        draft.playerSettings = action.payload;
        break;

      case USER_ACTION_TYPE.SET_CURRENT_PAYMENT_METHOD_ID:
        draft.currentPaymentMethodId = action.payload.currentPaymentMethodId;
        break;

      default:
        break;
    }
  });
